import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { ROLES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const UserDialog = ({ open, onOpenChange, onSaved, riders, existing }) => {
  const [f, setF] = useState(existing ? { name: existing.name, email: existing.email, role: existing.role, password: "", rider_id: existing.rider_id || "", qualification: existing.qualification || "" } : { name: "", email: "", role: "assessor", password: "", rider_id: "", qualification: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const passed = riders.filter((r) => r.last_pass_date);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = existing
        ? await api.patch(`/users/${existing.id}`, { name: f.name, role: f.role, rider_id: f.rider_id || null, qualification: f.qualification })
        : await api.post("/users", { ...f, rider_id: f.rider_id || null });
      toast.success(existing ? "User updated" : "User created");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{existing ? "Edit user" : "Create user"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Role</Label>
            <Select value={f.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ROLES).filter(([k]) => k !== "hospital" || f.role === "hospital").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.role === "rider" && (
            <div className="space-y-1.5"><Label>Rider record (passed riders only)</Label>
              <Select value={f.rider_id || undefined} onValueChange={(v) => { const r = passed.find((x) => x.id === v); setF({ ...f, rider_id: v, name: f.name || `${r.first_name} ${r.last_name}`, email: f.email || r.email || "" }); }}>
                <SelectTrigger data-testid="user-rider-select"><SelectValue placeholder={passed.length ? "Select rider" : "No passed riders yet"} /></SelectTrigger>
                <SelectContent>{passed.map((r) => <SelectItem key={r.id} value={r.id}>{r.first_name} {r.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Full name</Label><Input required value={f.name} onChange={set("name")} data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input required disabled={!!existing} type="email" value={f.email} onChange={set("email")} data-testid="user-email-input" /></div>
          {["assessor", "admin"].includes(f.role) && (
            <div className="space-y-1.5"><Label>Assessor qualification</Label><Input placeholder="e.g. IAM National Observer" value={f.qualification} onChange={set("qualification")} data-testid="user-qualification-input" /></div>
          )}
          {!existing && <div className="space-y-1.5"><Label>Temporary password (min 8 characters)</Label><Input required minLength={8} value={f.password} onChange={set("password")} data-testid="user-password-input" /></div>}
          <div className="flex justify-end"><button type="submit" data-testid="user-save-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">{existing ? "Save changes" : "Create user"}</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [riders, setRiders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/riders").then((r) => setRiders(r.data));
  }, []);

  const patch = async (u, body, msg) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, body);
      setUsers((x) => x.map((y) => (y.id === u.id ? data : y)));
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.name} (min 8 characters)`);
    if (pw) patch(u, { password: pw }, "Password reset");
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((x) => x.filter((y) => y.id !== u.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Administration</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Users</h1>
        </div>
        <button data-testid="add-user-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"><Plus className="h-4 w-4" /> Create user</button>
      </div>
      <div className="overflow-hidden rounded-2xl border bg-card" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 last:border-0">
            <div className="min-w-0">
              <div className="font-semibold">{u.name} {!u.active && <span className="ml-2 text-xs text-red-400">Disabled</span>}</div>
              <div className="truncate text-xs text-muted-foreground">{u.email} · {ROLES[u.role]}{u.qualification ? ` · ${u.qualification}` : ""}</div>
            </div>
            <div className="flex items-center gap-3">
              {u.id !== me.id && <Switch checked={u.active} onCheckedChange={(v) => patch(u, { active: v }, v ? "Enabled" : "Disabled")} data-testid={`user-active-${u.id}`} />}
              <button onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`} className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Edit"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => resetPw(u)} data-testid={`user-reset-${u.id}`} className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground" title="Reset password"><KeyRound className="h-4 w-4" /></button>
              {u.id !== me.id && <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="rounded-full p-2 text-red-400 hover:bg-red-500/10" title="Delete"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </div>
      {open && <UserDialog open={open} onOpenChange={setOpen} riders={riders} onSaved={(u) => setUsers((x) => [...x, u])} />}
      {editing && <UserDialog open onOpenChange={() => setEditing(null)} existing={editing} riders={riders} onSaved={(u) => setUsers((x) => x.map((y) => (y.id === u.id ? u : y)))} />}
    </div>
  );
}
